import { isNull, set } from 'lodash/fp';
import React, { useCallback, useMemo } from 'react';

import { formatDate, UTC_FORMAT } from '../../../../../utils/luxon';
import { initialDateFilter } from '../../../../constants/instanceFilters';
import { DatePicker } from '../../../../shared/datepicker/DatePicker';
import { Icon } from '../../../../shared/icon/Icon';
import { Delete } from '../../../../shared/icons';
import { CustomTooltip } from '../../../../shared/tooltip';
import { DateFilterType } from '../../store/filterTypes';
import styles from './TableInstanceFilters.module.scss';

interface DateFilterProps {
    updateFilter: (value: DateFilterType) => void;
    removeFilter: () => void;
    filter: DateFilterType | null;
    testId: string;
}

export const DateFilter: React.FC<DateFilterProps> = ({ updateFilter, removeFilter, filter, testId }) => {
    const dateFilter = useMemo(() => isNull(filter) ? initialDateFilter : filter, [filter]);
    const { from, to } = dateFilter;

    const fromDateValue = useMemo(() => from ? new Date(from) : null, [from]);
    const toDateValue = useMemo(() => to ? new Date(to) : null, [to]);

    const updateDateFilter = useCallback((key: keyof DateFilterType, value: string | null) => {
        if ((key === 'from' && isNull(value) && isNull(to)) || (key === 'to' && isNull(value) && isNull(from))) {
            removeFilter();
            return;
        }
        const updatedFilter = set(key, value, dateFilter);
        updateFilter(updatedFilter);
    }, [removeFilter, updateFilter, dateFilter, to, from]);

    const clearDateFilter = useCallback((key: keyof DateFilterType) => updateDateFilter(key, null), [updateDateFilter]);
    const clearFromDate = useCallback(() => clearDateFilter('from'), [clearDateFilter]);
    const clearToDate = useCallback(() => clearDateFilter('to'), [clearDateFilter]);
    const updateFromDate = useCallback((value: Date | null) => updateDateFilter('from', value ? formatDate(value, UTC_FORMAT) : null), [updateDateFilter]);
    const updateToDate = useCallback((value: Date | null) => updateDateFilter('to', value ? formatDate(value, UTC_FORMAT) : null), [updateDateFilter]);

    return (
        <div className={styles.dateFilterWrapper}>
            <div className={styles.dateValueWrapper} style={{ marginBottom: '10px' }}>
                <div className={styles.dateHeader}>From</div>
                <DatePicker
                    value={fromDateValue}
                    onChange={updateFromDate}
                    testId={`${testId}-from`}
                    maxDate={new Date()}
                />
                {!isNull(from) &&
                    <CustomTooltip overlayText='Clear Filter'>
                        <button onClick={clearFromDate} className={styles.clearFilterButton} style={{ borderLeft: 'none' }}><Icon icon={Delete} fontSize={18} /></button>
                    </CustomTooltip>
                }
            </div>
            <div className={styles.dateValueWrapper}>
                <div className={styles.dateHeader}>To</div>
                <DatePicker
                    value={toDateValue}
                    onChange={updateToDate}
                    testId={`${testId}-to`}
                    maxDate={new Date()}
                />
                {!isNull(to) &&
                    <CustomTooltip overlayText='Clear Filter'>
                        <button onClick={clearToDate} className={styles.clearFilterButton} style={{ borderLeft: 'none' }}><Icon icon={Delete} fontSize={18} /></button>
                    </CustomTooltip>
                }
            </div>
        </div>
    );
};
